// CE-124 — Report Permission Configuration (FE)
// Pure helpers: GET response → form cards, form cards → PATCH payload, the slot
// inherit/override merge, and client-side validation mirroring the backend rules.

import { ROLES } from '../../consts/roles'
import {
  type CardError,
  type PermissionCardForm,
  QuestionType,
  type QuestionTypeOption,
  ReportAccess,
  type ReportPermissionGroup,
  type ReportPermissionGroupPayload,
  ReportRoleType
} from './types'

/** Fixed options for the Visible Question Types (R2) multi-select. */
export const QUESTION_TYPE_OPTIONS: QuestionTypeOption[] = [
  { id: QuestionType.QUANTITATIVE, label: 'Quantitative Results' },
  { id: QuestionType.QUALITATIVE, label: 'Qualitative Results' }
]

const questionTypesFromFlags = (quantitative: boolean, qualitative: boolean): QuestionTypeOption[] =>
  QUESTION_TYPE_OPTIONS.filter((opt) =>
    (opt.id === QuestionType.QUANTITATIVE && quantitative) ||
    (opt.id === QuestionType.QUALITATIVE && qualitative))

const hasQuestionType = (card: PermissionCardForm, id: QuestionType): boolean =>
  card.questionTypes.some((q) => q.id === id)

/** A blank card seeded with the contract defaults (Full Access, both question types on). */
export const makeEmptyCard = (): PermissionCardForm => ({
  roleType: '',
  roles: [],
  reportAccess: ReportAccess.FULL_ACCESS,
  questionTypes: [...QUESTION_TYPE_OPTIONS],
  canViewTaResults: true
})

/** Map a server group into an editable form card. */
export const groupToCard = (group: ReportPermissionGroup): PermissionCardForm => ({
  groupId: group.groupId,
  roleType: group.roleType,
  roles: group.roles ?? [],
  reportAccess: group.reportAccess ?? ReportAccess.FULL_ACCESS,
  questionTypes: questionTypesFromFlags(group.visibleQuantitative ?? true, group.visibleQualitative ?? true),
  canViewTaResults: group.canViewTaResults ?? true
})

export const groupsToCards = (groups: ReportPermissionGroup[] = []): PermissionCardForm[] =>
  groups.map(groupToCard)

/**
 * True for a course-section card with at least one role, **unless** a Teaching Assistant is
 * selected — TAs can't view other TAs' results, so selecting one hides R3 (and the payload
 * then sends `canViewTaResults: false`). Observer, Support Staff, Instructors, etc. all
 * keep the control. Also false when Report Access is No Access — R3 governs visibility of
 * TA results *within* reports this role can already see, so it's moot (and hidden, same as
 * R2) when the role can't view reports at all.
 */
export const cardShowsTaResults = (card: PermissionCardForm): boolean =>
  card.roleType === ReportRoleType.COURSE_SECTION &&
  card.reportAccess !== ReportAccess.NO_ACCESS &&
  card.roles.length > 0 &&
  !card.roles.some((r) => r.roleName === ROLES.TEACHING_ASSISTANT)

/** Map one form card to a PATCH payload group. R3 is course-section only (omitted for academic unit). */
export const cardToPayload = (card: PermissionCardForm): ReportPermissionGroupPayload => {
  const payload: ReportPermissionGroupPayload = {
    roleType: card.roleType as ReportRoleType,
    roleIds: card.roles.map((r) => r.roleId),
    reportAccess: card.reportAccess,
    visibleQuantitative: hasQuestionType(card, QuestionType.QUANTITATIVE),
    visibleQualitative: hasQuestionType(card, QuestionType.QUALITATIVE)
  }
  if (card.roleType === ReportRoleType.COURSE_SECTION) {
    // R3 is only meaningful for instructor-only selections; otherwise force false.
    payload.canViewTaResults = cardShowsTaResults(card) ? card.canViewTaResults : false
  }
  return payload
}

export const cardsToPayload = (cards: PermissionCardForm[]): ReportPermissionGroupPayload[] =>
  cards.map(cardToPayload)

/**
 * Role ids already used by cards other than `currentIndex`. Drives the multi-select's
 * `getOptionDisabled` so a role can't be placed in two cards (DB enforces the same).
 */
export const takenRoleIds = (cards: PermissionCardForm[], currentIndex: number): Set<string> => {
  const taken = new Set<string>()
  cards.forEach((card, index) => {
    if (index === currentIndex) return
    card.roles.forEach((r) => taken.add(r.roleId))
  })
  return taken
}

/** R2 requires ≥1 question type unless access is No Access (where R2 is moot). */
export const isQuestionTypesValid = (card: PermissionCardForm): boolean => {
  if (card.reportAccess === ReportAccess.NO_ACCESS) return true
  return card.questionTypes.length > 0
}

const QUESTION_TYPES_ERROR =
  'Select at least one question type, or set Report Access to No Access.'
const ROLES_REQUIRED_ERROR = 'Select at least one role.'

/** Per-card validation messages. */
export const validateCard = (card: PermissionCardForm): CardError => {
  const error: CardError = {}
  if (!card.roleType || card.roles.length === 0) {
    error.roles = ROLES_REQUIRED_ERROR
  }
  if (!isQuestionTypesValid(card)) {
    error.questionTypes = QUESTION_TYPES_ERROR
  }
  return error
}

export const validateCards = (cards: PermissionCardForm[]): CardError[] => cards.map(validateCard)

/** True when every card is valid (nothing to save otherwise). */
export const areCardsValid = (cards: PermissionCardForm[]): boolean =>
  validateCards(cards).every((e) => e.roles === undefined && e.questionTypes === undefined)

/**
 * The series-default groups a slot is currently *inheriting* — i.e. project defaults minus
 * any role the slot already overrides (present in `slotCards`). A default group whose roles
 * are all overridden drops out. Recomputed live as the admin adds/edits slot overrides, so
 * a role moves from the inherited section into an editable card the moment it's overridden.
 */
export const inheritedCardsForSlot = (
  defaultCards: PermissionCardForm[],
  slotCards: PermissionCardForm[]
): PermissionCardForm[] => {
  const overridden = new Set<string>()
  slotCards.forEach((card) => {
    card.roles.forEach((r) => overridden.add(r.roleId))
  })
  return defaultCards
    .map((card) => ({ ...card, roles: card.roles.filter((r) => !overridden.has(r.roleId)) }))
    .filter((card) => card.roles.length > 0)
}
